
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import {
  HiOutlineScale,
  HiOutlineArrowUpRight,
  HiOutlineArrowLongLeft,
  HiOutlineChatBubbleLeftRight,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentCheck,
  HiOutlineShieldCheck,
  HiOutlineChevronRight,
  HiOutlineBriefcase,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import ServiceImage from "../components/services/ServiceImage";
import RelatedServices from "../components/services/RelatedServices";

/* ============================================
   ARTICLE-FREE SERVICE CONTENT
============================================ */

function ServiceContent({ text, emptyLabel }) {
  const value = typeof text === "string" ? text : "";

  const hasHTML = /<\/?[a-z][^>]*>/i.test(value);

  const safeHTML = useMemo(
    () =>
      DOMPurify.sanitize(value, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: [
          "script",
          "style",
          "iframe",
          "form",
        ],
        FORBID_ATTR: ["style"],
      }),
    [value]
  );

  const classes = `
    max-w-[82ch]
    break-words
    font-kufi
    text-base
    leading-[2.15]
    text-skin-text

    md:text-[17px]
    md:leading-[2.2]

    [&_p]:mb-6
    [&_p]:leading-[2.15]

    [&_h2]:mb-5
    [&_h2]:mt-9
    [&_h2]:text-2xl
    [&_h2]:font-bold
    [&_h2]:text-skin-heading

    [&_h3]:mb-4
    [&_h3]:mt-8
    [&_h3]:text-xl
    [&_h3]:font-semibold
    [&_h3]:text-skin-heading

    [&_strong]:font-bold
    [&_strong]:text-skin-heading

    [&_ul]:mb-6
    [&_ul]:list-disc
    [&_ul]:ps-7

    [&_ol]:mb-6
    [&_ol]:list-decimal
    [&_ol]:ps-7

    [&_li]:mb-3

    [&_a]:break-words
    [&_a]:text-skin-accent
    [&_a]:underline

    [&_blockquote]:my-7
    [&_blockquote]:border-s-2
    [&_blockquote]:border-skin-accent
    [&_blockquote]:bg-skin-soft
    [&_blockquote]:px-6
    [&_blockquote]:py-5

    [&_img]:my-6
    [&_img]:max-w-full
    [&_img]:rounded-xl
  `;

  if (!value.trim()) {
    return (
      <p className="body-2 text-skin-muted">
        {emptyLabel}
      </p>
    );
  }

  if (hasHTML) {
    return (
      <div
        className={classes}
        dangerouslySetInnerHTML={{
          __html: safeHTML,
        }}
      />
    );
  }

  return (
    <div className={classes}>
      {value
        .split(/\n\s*\n/)
        .filter(Boolean)
        .map((paragraph, index) => (
          <p
            key={index}
            className="mb-6 whitespace-pre-line"
          >
            {paragraph.trim()}
          </p>
        ))}
    </div>
  );
}

/* ============================================
   SERVICE DETAILS PAGE
============================================ */

function ServiceDetails() {
  const location = useLocation();
  const { i18n } = useTranslation();

  const service = location.state?.ServiceData ?? null;

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = service
    ? (
        isEnglish
          ? service.titleEn || service.titleAr
          : service.titleAr || service.titleEn
      )
    : "";

  const description = service
    ? (
        isEnglish
          ? service.textEn || service.textAr
          : service.textAr || service.textEn
      )
    : "";

  const preview = useMemo(() => {
    const plain = DOMPurify.sanitize(
      String(description || ""),
      {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }
    )
      .replace(/\s+/g, " ")
      .trim();

    return plain.length > 245
      ? `${plain.slice(0, 245).trimEnd()}...`
      : plain;
  }, [description]);

  /* ======================================
     TRANSLATIONS
  ====================================== */

  const content = {
    home: isEnglish ? "Home" : "الرئيسية",

    services: isEnglish
      ? "Legal Services"
      : "الخدمات القانونية",

    eyebrow: isEnglish
      ? "OUR LEGAL PRACTICES"
      : "مجالات الممارسة القانونية",

    introLabel: isEnglish
      ? "Professional Legal Support"
      : "الدعم والاستشارة القانونية",

    back: isEnglish
      ? "Browse All Services"
      : "استعرض جميع الخدمات",

    detailsEyebrow: isEnglish
      ? "UNDERSTANDING THE SERVICE"
      : "تعرف على تفاصيل الخدمة",

    detailsTitle: isEnglish
      ? "Service Overview"
      : "نطاق الخدمة وتفاصيلها",

    detailsDescription: isEnglish
      ? "An overview of this legal service and the matters it covers."
      : "شرح للخدمة القانونية والمسائل التي تتناولها.",

    emptyDescription: isEnglish
      ? "Additional details are not available yet."
      : "لم تتم إضافة تفاصيل هذه الخدمة بعد.",

    processEyebrow: isEnglish
      ? "GETTING STARTED"
      : "خطوات البدء",

    processTitle: isEnglish
      ? "A Clear Way to Begin"
      : "من الاستفسار إلى الخطوة التالية",

    processDescription: isEnglish
      ? "Start by explaining your legal matter so the available options can be considered."
      : "ابدأ بتوضيح موضوعك القانوني لتحديد الخيارات والإجراءات المناسبة وفق ظروف المسألة.",

    steps: isEnglish
      ? [
          {
            title: "Share Your Inquiry",
            description:
              "Explain the nature of your legal matter and your main questions.",
            icon: HiOutlineChatBubbleLeftRight,
          },
          {
            title: "Identify Relevant Information",
            description:
              "Identify available facts and documents that may be relevant to your inquiry.",
            icon: HiOutlineDocumentText,
          },
          {
            title: "Discuss the Next Steps",
            description:
              "Review the potential options and appropriate next steps for your situation.",
            icon: HiOutlineClipboardDocumentCheck,
          },
        ]
      : [
          {
            title: "توضيح الاستفسار",
            description:
              "اشرح طبيعة المسألة القانونية والأسئلة التي ترغب في الحصول على إجابة عنها.",
            icon: HiOutlineChatBubbleLeftRight,
          },
          {
            title: "تحديد المعلومات المهمة",
            description:
              "حدد الوقائع والمستندات المتوفرة التي قد تكون ذات صلة بموضوع الاستفسار.",
            icon: HiOutlineDocumentText,
          },
          {
            title: "مناقشة الخطوة المناسبة",
            description:
              "ناقش الخيارات والخطوات المحتملة في ضوء طبيعة المسألة وظروفها.",
            icon: HiOutlineClipboardDocumentCheck,
          },
        ],

    ctaEyebrow: isEnglish
      ? "NEED LEGAL ASSISTANCE?"
      : "هل تحتاج إلى مساعدة قانونية؟",

    ctaTitle: isEnglish
      ? "Your Legal Inquiry Starts Here"
      : "ابدأ استفسارك القانوني بثقة ووضوح",

    ctaDescription: isEnglish
      ? "Contact Al Noman Law Office to inquire about this service and discuss your legal needs."
      : "تواصل مع مؤسسة النعمان للاستفسار عن هذه الخدمة ومناقشة احتياجاتك القانونية.",

    contact: isEnglish
      ? "Contact via WhatsApp"
      : "تواصل عبر واتساب",

    missingTitle: isEnglish
      ? "Service Details Are Unavailable"
      : "تعذر عرض تفاصيل الخدمة",

    missingDescription: isEnglish
      ? "Please return to the services page and select the service you wish to explore."
      : "يرجى العودة إلى صفحة الخدمات واختيار الخدمة التي ترغب في الاطلاع على تفاصيلها.",
  };

  /* ======================================
     WHATSAPP CONTACT
  ====================================== */

  const whatsappMessage = isEnglish
    ? `Hello, I would like to inquire about the legal service: ${title}`
    : `السلام عليكم، أرغب في الاستفسار عن الخدمة القانونية: ${title}`;

  const whatsappUrl =
    `https://wa.me/967777332887?text=${encodeURIComponent(
      whatsappMessage
    )}`;

  /* ======================================
     MISSING SERVICE STATE
  ====================================== */

  if (!service) {
    return (
      <>
        <Header active={3} />

        <main className="flex min-h-[65vh] items-center justify-center bg-skin-page px-5 py-20">
          <div className="mx-auto max-w-[560px] text-center">

            <div className="mx-auto mb-7 flex h-20 w-20 items-center justify-center rounded-2xl border border-skin-border bg-skin-surface text-skin-accent">
              <HiOutlineBriefcase size={35} />
            </div>

            <h1 className="h3 text-skin-heading">
              {content.missingTitle}
            </h1>

            <p className="body-2 mt-5 text-skin-muted">
              {content.missingDescription}
            </p>

            <Link
              to="/AllServiceses"
              className="button mt-8 inline-flex min-h-[52px] items-center gap-3 rounded-xl bg-brand-navy px-6 py-3 text-white transition-colors hover:bg-brand-navyLight dark:border dark:border-white/15 dark:bg-white/10 dark:hover:bg-white/15"
            >
              {content.back}

              <HiOutlineArrowUpRight
                size={19}
                className="directional-arrow"
              />
            </Link>

          </div>
        </main>

        <Fotter />
      </>
    );
  }

  return (
    <>
      <Header active={3} />

      <main className="overflow-hidden bg-skin-page text-skin-text">

        {/* ======================================
            01 — SERVICE HERO
        ====================================== */}

        <section
          className="
            relative isolate overflow-hidden
            border-b border-skin-border
            bg-[#EAF0F5]
            py-12
            md:py-16
            lg:py-20

            dark:bg-[#102031]
            dark:border-white/10
          "
        >
          {/* Subtle decorative detail */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-40
              h-[450px] w-[450px]
              rounded-full
              bg-white/50 blur-3xl
              dark:bg-[#294961]/20
            "
          />

          <div className="container relative">

            {/* Breadcrumb */}
            <nav
              aria-label={
                isEnglish ? "Breadcrumb" : "مسار التنقل"
              }
              className="mb-12 flex flex-wrap items-center gap-2 text-skin-muted lg:mb-16"
            >
              <Link
                to="/"
                className="caption hover:text-skin-heading"
              >
                {content.home}
              </Link>

              <HiOutlineChevronRight
                size={15}
                className="directional-arrow"
                aria-hidden="true"
              />

              <Link
                to="/AllServiceses"
                className="caption hover:text-skin-heading"
              >
                {content.services}
              </Link>

              <HiOutlineChevronRight
                size={15}
                className="directional-arrow"
                aria-hidden="true"
              />

              <span className="caption max-w-[250px] truncate font-semibold text-skin-heading sm:max-w-none">
                {title}
              </span>
            </nav>

            {/* Main hero grid */}
            <div
              className="
                grid items-center
                gap-12
                lg:grid-cols-2
                lg:gap-16
                xl:gap-24
              "
            >
              {/* ==========================
                  TEXT CONTENT
              ========================== */}

              <div className="min-w-0">

                {/* Eyebrow */}
                <div className="mb-6 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="h-px w-10 bg-skin-accent"
                  />

                  <span className="eyebrow text-skin-accent">
                    {content.eyebrow}
                  </span>

                </div>

                {/* Title */}
                <h1
                  className="
                    h1 max-w-[740px]
                    text-skin-heading
                  "
                >
                  {title}
                </h1>

                {/* Accent */}
                <div
                  aria-hidden="true"
                  className="mt-7 h-[3px] w-16 rounded-full bg-skin-accent"
                />

                {/* Description */}
                <p
                  className="
                    body-1 mt-7
                    max-w-[650px]
                    text-skin-text
                  "
                >
                  {preview || content.detailsDescription}
                </p>

                {/* Quick actions */}
                <div
                  className="
                    mt-10 flex flex-wrap
                    items-center gap-3
                  "
                >
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      group inline-flex min-h-[54px]
                      items-center justify-center gap-3
                      rounded-xl border border-brand-navy
                      bg-brand-navy px-6 py-3
                      text-white
                      shadow-soft
                      transition-all duration-300

                      hover:-translate-y-0.5
                      hover:bg-brand-navyLight

                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-skin-accent

                      dark:border-white/20
                      dark:bg-white/10
                      dark:backdrop-blur-xl
                      dark:hover:bg-white/15
                    "
                  >
                    <HiOutlineChatBubbleLeftRight
                      size={21}
                      aria-hidden="true"
                    />

                    <span className="button-sm">
                      {content.contact}
                    </span>

                    <HiOutlineArrowUpRight
                      size={18}
                      className="directional-arrow"
                      aria-hidden="true"
                    />
                  </a>

                  <Link
                    to="/AllServiceses"
                    onClick={() => window.scrollTo(0, 0)}
                    className="
                      group inline-flex min-h-[54px]
                      items-center justify-center gap-3
                      rounded-xl
                      border border-skin-border
                      bg-skin-surface/75
                      px-6 py-3
                      text-skin-heading
                      transition-all duration-300

                      hover:border-skin-accent/40
                      hover:bg-skin-surface
                    "
                  >
                    <HiOutlineArrowLongLeft
                      size={19}
                      className="directional-arrow"
                      aria-hidden="true"
                    />

                    <span className="button-sm">
                      {content.back}
                    </span>
                  </Link>

                </div>
              </div>

              {/* ==========================
                  IMAGE CONTENT
              ========================== */}

              <div className="relative min-w-0">

                {/* Decorative border */}
                <div
                  aria-hidden="true"
                  className="
                    absolute -end-4 -top-4
                    h-36 w-36
                    border-e-2 border-t-2
                    border-skin-accent/65
                  "
                />

                <div
                  className="
                    group relative
                    overflow-hidden
                    rounded-[24px]
                    border-[6px]
                    border-white

                    bg-[#142C40]

                    shadow-[0_28px_65px_rgba(15,39,55,0.13)]

                    dark:border-[#294359]
                    dark:shadow-[0_28px_65px_rgba(0,0,0,0.20)]
                  "
                >
                  <ServiceImage
                    image={service.Image}
                    alt={title}
                    priority
                    className="
                      aspect-[5/4] w-full
                      sm:aspect-[6/5]
                      lg:aspect-[5/5]
                    "
                  />

                  {/* Image gradient */}
                  <div
                    aria-hidden="true"
                    className="
                      pointer-events-none
                      absolute inset-0
                      bg-gradient-to-t
                      from-[#071522]/70
                      via-transparent
                      to-transparent
                    "
                  />

                  {/* Small image label */}
                  <div
                    className="
                      absolute bottom-5 start-5
                      flex max-w-[90%]
                      items-center gap-3

                      rounded-xl
                      border border-white/20
                      bg-[#102031]/65
                      px-5 py-4

                      text-white
                      backdrop-blur-xl

                      sm:bottom-7 sm:start-7
                    "
                  >
                    <HiOutlineScale
                      size={25}
                      className="shrink-0 text-[#D7B77F]"
                    />

                    <span className="body-3 text-white/90">
                      {content.introLabel}
                    </span>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 — SERVICE DETAILS
        ====================================== */}

        <section
          id="service-information"
          className="
            bg-skin-page
            py-16
            md:py-20
            lg:py-24
          "
        >
          <div className="container">

            <div
              className="
                grid items-start
                gap-10
                lg:grid-cols-[260px_minmax(0,1fr)]
                lg:gap-20
              "
            >
              {/* Editorial label */}
              <div className="lg:pt-2">

                <div className="mb-6 flex items-center gap-3">
                  <span className="h-px w-9 bg-skin-accent" />

                  <span className="eyebrow text-skin-accent">
                    {content.detailsEyebrow}
                  </span>
                </div>

                <h2 className="h3 text-skin-heading">
                  {content.detailsTitle}
                </h2>

                <p className="body-3 mt-5 text-skin-muted">
                  {content.detailsDescription}
                </p>

                <div
                  aria-hidden="true"
                  className="mt-8 hidden h-px w-20 bg-skin-border lg:block"
                />
              </div>

              {/* Actual text from MongoDB */}
              <div
                className="
                  min-w-0
                  border-t-2 border-skin-accent/50
                  pt-8
                "
              >
                <ServiceContent
                  text={description}
                  emptyLabel={content.emptyDescription}
                />
              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            03 — PROCESS STEPS
        ====================================== */}

        <section
          className="
            border-y border-skin-border
            bg-skin-surface
            py-16
            md:py-20
            lg:py-24
          "
        >
          <div className="container">

            {/* Section heading */}
            <div className="max-w-[760px]">

              <div className="mb-5 flex items-center gap-3">
                <span className="h-px w-9 bg-skin-accent" />

                <span className="eyebrow text-skin-accent">
                  {content.processEyebrow}
                </span>
              </div>

              <h2 className="section-title text-skin-heading">
                {content.processTitle}
              </h2>

              <p className="body-2 mt-5 text-skin-muted">
                {content.processDescription}
              </p>
            </div>

            {/* Process grid */}
            <div
              className="
                mt-12 grid
                grid-cols-1 gap-5
                md:grid-cols-3
                lg:gap-7
              "
            >
              {content.steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <article
                    key={index}
                    className="
                      group relative
                      flex h-full flex-col

                      rounded-[20px]
                      border border-skin-border
                      bg-skin-page

                      p-7

                      transition-all duration-300

                      hover:-translate-y-1
                      hover:border-skin-accent/35

                      sm:p-8
                    "
                  >
                    {/* Number and icon */}
                    <div className="mb-9 flex items-start justify-between">

                      <div
                        className="
                          flex h-13 w-13
                          h-[52px] w-[52px]
                          items-center justify-center
                          rounded-xl
                          bg-skin-soft
                          text-skin-accent
                        "
                      >
                        <Icon
                          size={26}
                          aria-hidden="true"
                        />
                      </div>

                      <span
                        className="
                          font-cairo text-2xl
                          font-bold tabular-nums
                          text-skin-border
                          dark:text-white/15
                        "
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>

                    </div>

                    <h3 className="h6 text-skin-heading">
                      {step.title}
                    </h3>

                    <p className="body-2 mt-4 text-skin-muted">
                      {step.description}
                    </p>

                    {/* Bottom accent */}
                    <div
                      aria-hidden="true"
                      className="
                        mt-9 h-[2px] w-9
                        rounded-full bg-skin-accent/65

                        transition-all duration-300
                        group-hover:w-16
                      "
                    />

                  </article>
                );
              })}
            </div>

          </div>
        </section>

        {/* ======================================
            04 — CONTACT CTA
        ====================================== */}

        <section className="bg-skin-page py-14 md:py-20">
          <div className="container">

            <div
              className="
                relative isolate
                overflow-hidden
                rounded-[26px]

                bg-[#19344D]
                px-7 py-10
                text-white

                sm:px-10 sm:py-12
                lg:px-14 lg:py-14
              "
            >
              {/* Decorative circle */}
              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute -end-20 -top-28
                  -z-10
                  h-80 w-80
                  rounded-full
                  border border-white/10
                "
              />

              <div
                className="
                  flex flex-col
                  items-start gap-8

                  lg:flex-row
                  lg:items-center
                  lg:justify-between
                  lg:gap-12
                "
              >
                {/* Text */}
                <div className="max-w-[730px]">

                  <div className="mb-5 flex items-center gap-3">
                    <HiOutlineShieldCheck
                      size={22}
                      className="text-[#D7B77F]"
                    />

                    <span className="eyebrow text-[#D7B77F]">
                      {content.ctaEyebrow}
                    </span>
                  </div>

                  <h2 className="h3 text-white">
                    {content.ctaTitle}
                  </h2>

                  <p className="body-2 mt-5 text-[#C6D3DE]">
                    {content.ctaDescription}
                  </p>
                </div>

                {/* WhatsApp action */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    group inline-flex min-h-[54px]
                    shrink-0 items-center justify-center
                    gap-4

                    rounded-xl
                    border border-white/20
                    bg-white/[0.10]

                    px-6 py-3
                    text-white

                    backdrop-blur-xl
                    transition-all duration-300

                    hover:border-white/40
                    hover:bg-white/[0.17]

                    focus-visible:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-[#D7B77F]
                  "
                >
                  <span className="button-sm">
                    {content.contact}
                  </span>

                  <HiOutlineArrowUpRight
                    size={20}
                    aria-hidden="true"
                    className="directional-arrow text-[#D7B77F]"
                  />
                </a>

              </div>
            </div>

          </div>
        </section>

        {/* ======================================
            05 — RELATED SERVICES
        ====================================== */}

        <RelatedServices currentServiceId={service._id} />

      </main>

      <Fotter />
    </>
  );
}

export default ServiceDetails;
